import { create } from 'zustand';
import {
  getSurveys,
  getFormById,
  createForm,
  updateForm,
  deleteForm,
  getAnalyticsStats,
  getBarChartData,
  getRatingDistribution,
  getRecentResponses,
  getSurveyOptions,
  Survey,
  FeedbackForm,
  AnalyticsStat,
  BarChartDataItem,
  RatingDistributionItem,
  RecentResponse,
  SurveyOption,
} from '@/services/PagesServices/FeedbackManagementService';

interface FeedbackManagementState {
  surveys: Survey[] | null;
  currentForm: FeedbackForm | null;
  editingFormId: string | null;
  analyticsStats: AnalyticsStat[] | null;
  barChartData: BarChartDataItem[] | null;
  ratingDistribution: RatingDistributionItem[] | null;
  recentResponses: RecentResponse[] | null;
  surveyOptions: SurveyOption[] | null;
  selectedFormId: string | null;
  loading: boolean;
  saving: boolean;
  error: string | null;

  // Form CRUD actions
  fetchSurveys: () => Promise<void>;
  fetchFormById: (id: string) => Promise<void>;
  createForm: (formData: Omit<FeedbackForm, 'id' | 'uuid' | 'slug'>) => Promise<{ success: boolean; data?: any; error?: string }>;
  updateForm: (id: string, formData: Partial<FeedbackForm>) => Promise<{ success: boolean; error?: string }>;
  deleteForm: (id: string) => Promise<{ success: boolean; error?: string }>;
  setEditingFormId: (id: string | null) => void;
  clearCurrentForm: () => void;

  // Analytics actions
  setSelectedFormId: (id: string | null) => void;
  fetchAnalyticsStats: (formId?: string) => Promise<void>;
  fetchBarChartData: (formId?: string) => Promise<void>;
  fetchRatingDistribution: (formId?: string) => Promise<void>;
  fetchRecentResponses: (formId?: string) => Promise<void>;
  fetchSurveyOptions: () => Promise<void>;
  fetchAllAnalytics: (formId?: string) => Promise<void>;
}

const useFeedbackManagementStore = create<FeedbackManagementState>((set, get) => ({
  surveys: null,
  currentForm: null,
  editingFormId: null,
  analyticsStats: null,
  barChartData: null,
  ratingDistribution: null,
  recentResponses: null,
  surveyOptions: null,
  selectedFormId: null,
  loading: false,
  saving: false,
  error: null,

  // ===== FORM CRUD ACTIONS =====

  fetchSurveys: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getSurveys();
      set({ surveys: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchFormById: async (id: string) => {
    try {
      set({ loading: true, error: null });
      const response = await getFormById(id);
      set({ currentForm: response.data || null, loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  createForm: async (formData) => {
    try {
      set({ saving: true, error: null });
      const response = await createForm(formData);
      if (response.success) {
        // Refresh surveys list
        get().fetchSurveys();
      }
      set({ saving: false });
      return { success: response.success, data: response.data, error: response.error?.message };
    } catch (error: any) {
      set({ error: error.message, saving: false });
      return { success: false, error: error.message };
    }
  },

  updateForm: async (id, formData) => {
    try {
      set({ saving: true, error: null });
      const response = await updateForm(id, formData);
      if (response.success) {
        get().fetchSurveys();
      }
      set({ saving: false });
      return { success: response.success, error: response.error?.message };
    } catch (error: any) {
      set({ error: error.message, saving: false });
      return { success: false, error: error.message };
    }
  },

  deleteForm: async (id) => {
    try {
      set({ loading: true, error: null });
      const response = await deleteForm(id);
      if (response.success) {
        get().fetchSurveys();
      }
      set({ loading: false });
      return { success: response.success, error: response.error?.message };
    } catch (error: any) {
      set({ error: error.message, loading: false });
      return { success: false, error: error.message };
    }
  },

  setEditingFormId: (id: string | null) => {
    set({ editingFormId: id });
    if (id) {
      get().fetchFormById(id);
    } else {
      set({ currentForm: null });
    }
  },

  clearCurrentForm: () => set({ currentForm: null, editingFormId: null }),

  // ===== ANALYTICS ACTIONS =====

  setSelectedFormId: (id: string | null) => {
    set({ selectedFormId: id });
    if (id) {
      get().fetchAllAnalytics(id);
    }
  },

  fetchAnalyticsStats: async (formId?: string) => {
    try {
      set({ loading: true, error: null });
      const id = formId || get().selectedFormId || undefined;
      const response = await getAnalyticsStats(id);
      set({ analyticsStats: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchBarChartData: async (formId?: string) => {
    try {
      set({ loading: true, error: null });
      const id = formId || get().selectedFormId || undefined;
      const response = await getBarChartData(id);
      set({ barChartData: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchRatingDistribution: async (formId?: string) => {
    try {
      set({ loading: true, error: null });
      const id = formId || get().selectedFormId || undefined;
      const response = await getRatingDistribution(id);
      set({ ratingDistribution: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchRecentResponses: async (formId?: string) => {
    try {
      set({ loading: true, error: null });
      const id = formId || get().selectedFormId || undefined;
      const response = await getRecentResponses(id);
      set({ recentResponses: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchSurveyOptions: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getSurveyOptions();
      set({ surveyOptions: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchAllAnalytics: async (formId?: string) => {
    const id = formId || get().selectedFormId || undefined;
    await Promise.all([
      get().fetchAnalyticsStats(id),
      get().fetchBarChartData(id),
      get().fetchRatingDistribution(id),
      get().fetchRecentResponses(id),
    ]);
  },
}));

export default useFeedbackManagementStore;
